import React, { useEffect, useState } from "react";
import { httpsCallable } from "firebase/functions";
import { doc, getDoc } from "firebase/firestore";
import { db, functions } from "../firebase";
import { useSubscription } from "../contexts/SubscriptionContext";
import { useAccountOptional } from "../contexts/AccountContext";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../design/button";
import { Heading, Subheading } from "../design/heading";
import { Text } from "../design/text";
import { Account } from "@visitproof/types";

interface OtherAccountInfo {
  id: string;
  name: string;
  role: string;
  hasActiveSubscription: boolean;
}

/**
 * A gate component that blocks access to children when subscription is not active.
 * Shows a subscription required screen when the trial has expired or subscription is canceled.
 */
const SELECTED_ACCOUNT_KEY = 'visitproof_selected_account';

export const SubscriptionGate: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const subscription = useSubscription();
  const accountContext = useAccountOptional();
  const { user, activeRole, claims } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [otherAccounts, setOtherAccounts] = useState<OtherAccountInfo[]>([]);
  const [loadingAccounts, setLoadingAccounts] = useState(false);

  // Determine if the current user is the account owner
  const isOwner = activeRole === 'owner' || (user && accountContext?.account?.adminId === user.uid);

  // Get current account ID
  const currentAccountId = accountContext?.account?.id;

  // Get all account IDs from claims
  const allAccountIds = claims?.accountIds as string[] | undefined;
  const hasMultipleAccounts = allAccountIds && allAccountIds.length > 1;

  // Load other accounts info when blocked and user has multiple accounts
  useEffect(() => {
    if (!subscription.shouldBlockAccess || !hasMultipleAccounts || !currentAccountId) {
      setOtherAccounts([]);
      return;
    }

    const loadOtherAccounts = async () => {
      setLoadingAccounts(true);
      const accounts: OtherAccountInfo[] = [];

      for (const accountId of allAccountIds) {
        if (accountId === currentAccountId) continue;

        try {
          const accountDoc = await getDoc(doc(db, 'accounts', accountId));
          if (accountDoc.exists()) {
            const data = accountDoc.data() as Account;
            const accountClaims = claims?.accounts?.[accountId];
            const role = accountClaims?.role || accountClaims?.roles?.[0] || 'member';
            const status = data.stripeStatus;
            const hasActiveSubscription = status === 'active' || status === 'trialing';

            accounts.push({
              id: accountId,
              name: data.name || 'Unknown Organization',
              role: role,
              hasActiveSubscription,
            });
          }
        } catch (err) {
          console.error('Error loading account:', accountId, err);
        }
      }

      setOtherAccounts(accounts);
      setLoadingAccounts(false);
    };

    loadOtherAccounts();
  }, [subscription.shouldBlockAccess, hasMultipleAccounts, currentAccountId, allAccountIds, claims?.accounts]);

  // Handle switching to another account
  const handleSwitchAccount = (accountId: string) => {
    localStorage.setItem(SELECTED_ACCOUNT_KEY, accountId);
    window.location.reload();
  };

  // While loading, don't block (show children)
  if (subscription.loading) {
    return <>{children}</>;
  }

  // Only block for admin users - supervisors and parents should still have access to view
  if (!accountContext?.isAdmin) {
    return <>{children}</>;
  }

  // If subscription is active, show children
  if (!subscription.shouldBlockAccess) {
    return <>{children}</>;
  }

  const handleSubscribe = async () => {
    setIsLoading(true);
    try {
      const result = await httpsCallable(
        functions,
        "generateCheckoutSession"
      )({});
      const data = result.data as { url: string };
      window.location.replace(data.url);
    } catch (error) {
      console.error("Error: ", JSON.stringify(error));
      alert(
        "Failed to generate checkout session. Please try again later."
      );
    }
    setIsLoading(false);
  };

  // Format trial end date
  const formattedTrialEnd = subscription.trialEndDate
    ? subscription.trialEndDate.toLocaleDateString("en-US", {
        month: "long",
        day: "numeric",
        year: "numeric",
      })
    : null;

  return (
    <div className="flex items-center justify-center min-h-screen bg-zinc-50 dark:bg-zinc-900">
      <div className="w-full max-w-lg p-8 mx-4 bg-white border shadow-xl dark:bg-zinc-800 rounded-2xl border-zinc-200 dark:border-zinc-700">
        {/* Icon */}
        <div className="flex justify-center mb-6">
          <div className="flex items-center justify-center w-16 h-16 rounded-full bg-red-100 dark:bg-red-900/30">
            <svg
              className="w-8 h-8 text-red-600 dark:text-red-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
              />
            </svg>
          </div>
        </div>

        {/* Content */}
        <div className="text-center">
          <Heading className="mb-2 text-zinc-900 dark:text-white">
            {isOwner ? "Subscription Required" : "Account Access Paused"}
          </Heading>

          {isOwner ? (
            // Owner messaging - encourage them to subscribe
            <>
              {subscription.isTrialExpired ? (
                <Text className="mb-6 text-zinc-600 dark:text-zinc-400">
                  Your 30-day free trial ended
                  {formattedTrialEnd ? ` on ${formattedTrialEnd}` : ""}. To continue
                  using VisitProof and manage your cases, visits, and supervisors,
                  please subscribe to a plan.
                </Text>
              ) : (
                <Text className="mb-6 text-zinc-600 dark:text-zinc-400">
                  Your subscription is no longer active. To continue using VisitProof
                  and manage your cases, visits, and supervisors, please subscribe to
                  a plan.
                </Text>
              )}

              {/* Features list */}
              <div className="p-4 mb-6 text-left rounded-lg bg-zinc-50 dark:bg-zinc-900/50">
                <Subheading className="mb-3 text-zinc-900 dark:text-white">
                  With your subscription, you can:
                </Subheading>
                <ul className="space-y-2 text-sm text-zinc-600 dark:text-zinc-400">
                  <li className="flex items-center gap-2">
                    <svg
                      className="w-4 h-4 text-green-500"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                        clipRule="evenodd"
                      />
                    </svg>
                    Create and manage cases
                  </li>
                  <li className="flex items-center gap-2">
                    <svg
                      className="w-4 h-4 text-green-500"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                        clipRule="evenodd"
                      />
                    </svg>
                    Schedule and track visits
                  </li>
                  <li className="flex items-center gap-2">
                    <svg
                      className="w-4 h-4 text-green-500"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                        clipRule="evenodd"
                      />
                    </svg>
                    Add parents, children, and supervisors
                  </li>
                  <li className="flex items-center gap-2">
                    <svg
                      className="w-4 h-4 text-green-500"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                        clipRule="evenodd"
                      />
                    </svg>
                    Generate professional reports
                  </li>
                  <li className="flex items-center gap-2">
                    <svg
                      className="w-4 h-4 text-green-500"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                        clipRule="evenodd"
                      />
                    </svg>
                    Access all premium features
                  </li>
                </ul>
              </div>

              {/* CTA Button */}
              <Button
                color="blue"
                disabled={isLoading}
                onClick={handleSubscribe}
                className="w-full py-3 text-base"
              >
                {isLoading ? (
                  <div className="flex items-center justify-center gap-2">
                    <svg
                      className="w-5 h-5 animate-spin"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      />
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      />
                    </svg>
                    <span>Loading...</span>
                  </div>
                ) : (
                  "Subscribe Now"
                )}
              </Button>

              {/* Support link */}
              <Text className="mt-4 text-sm text-zinc-500 dark:text-zinc-500">
                Need help?{" "}
                <a
                  href="mailto:support@visitproof.com"
                  className="text-blue-600 hover:underline dark:text-blue-400"
                >
                  Contact support
                </a>
              </Text>
            </>
          ) : (
            // Admin (non-owner) messaging - encourage them to contact the owner
            <>
              <Text className="mb-6 text-zinc-600 dark:text-zinc-400">
                Your organization's subscription is no longer active. Please reach out
                to your account owner to restore access to VisitProof.
              </Text>

              {/* Info box */}
              <div className="p-4 mb-6 text-left rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800">
                <div className="flex items-start gap-3">
                  <svg
                    className="w-5 h-5 text-amber-600 dark:text-amber-400 mt-0.5 flex-shrink-0"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                  <div>
                    <Text className="font-medium text-amber-800 dark:text-amber-200">
                      What you can do
                    </Text>
                    <Text className="mt-1 text-sm text-amber-700 dark:text-amber-300">
                      Contact your account owner and let them know the subscription needs
                      to be renewed. Once the subscription is active, you'll automatically
                      regain access.
                    </Text>
                  </div>
                </div>
              </div>

              {/* Support link */}
              <Text className="text-sm text-zinc-500 dark:text-zinc-500">
                If you believe this is an error,{" "}
                <a
                  href="mailto:support@visitproof.com"
                  className="text-blue-600 hover:underline dark:text-blue-400"
                >
                  contact support
                </a>
              </Text>
            </>
          )}

          {/* Other accounts section */}
          {hasMultipleAccounts && (
            <div className="pt-6 mt-6 border-t border-zinc-200 dark:border-zinc-700">
              <Text className="mb-3 text-sm font-medium text-zinc-700 dark:text-zinc-300">
                Switch to another organization
              </Text>
              {loadingAccounts ? (
                <Text className="text-sm text-zinc-500">Loading organizations...</Text>
              ) : otherAccounts.length > 0 ? (
                <div className="space-y-2">
                  {otherAccounts.map((account) => (
                    <button
                      key={account.id}
                      onClick={() => handleSwitchAccount(account.id)}
                      className="flex items-center justify-between w-full p-3 text-left transition-colors border rounded-lg border-zinc-200 dark:border-zinc-700 hover:bg-zinc-50 dark:hover:bg-zinc-700/50"
                    >
                      <div>
                        <Text className="font-medium text-zinc-900 dark:text-white">
                          {account.name}
                        </Text>
                        <Text className="text-xs text-zinc-500 dark:text-zinc-400">
                          {account.role.charAt(0).toUpperCase() + account.role.slice(1)}
                        </Text>
                      </div>
                      {account.hasActiveSubscription ? (
                        <span className="px-2 py-1 text-xs font-medium text-green-700 bg-green-100 rounded-full dark:bg-green-900/30 dark:text-green-400">
                          Active
                        </span>
                      ) : (
                        <span className="px-2 py-1 text-xs font-medium rounded-full text-zinc-600 bg-zinc-100 dark:bg-zinc-700 dark:text-zinc-400">
                          Inactive
                        </span>
                      )}
                    </button>
                  ))}
                </div>
              ) : (
                <Text className="text-sm text-zinc-500">No other organizations found.</Text>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SubscriptionGate;
